/**
 * How much the reported problem is hurting the customer right now. Ordered worst-first; this is the signal support
 * sorts and routes on, which is why it's a closed list rather than free text.
 *
 * Keep in sync with `ProblemImpact` in `DesignServer/Controllers/SupportController.cs`.
 */
export enum ProblemImpact {
  Blocked = 'Blocked',
  RepeatedRework = 'RepeatedRework',
  SlowWorkaround = 'SlowWorkaround',
  UntrustedOutput = 'UntrustedOutput',
  Cosmetic = 'Cosmetic',
}

/**
 * The wording customers actually see. Deliberately phrased as the customer's own cost rather than a severity level,
 * so the answer describes the painpoint instead of guessing at our priorities.
 *
 * `DescribeImpact` in `DesignServer/Controllers/SupportController.cs` repeats this list word for word — the
 * Slack message and the acknowledgement email both quote the customer's own answer rather than rephrasing it. Keep
 * the two lists identical.
 */
export const PROBLEM_IMPACT_LABELS: Readonly<Record<ProblemImpact, string>> = {
  [ProblemImpact.Blocked]: "Blocked — I can't finish this project because of this problem",
  [ProblemImpact.RepeatedRework]: 'I have to redo work every time this happens',
  [ProblemImpact.SlowWorkaround]: "I have a workaround, but it's slow",
  [ProblemImpact.UntrustedOutput]: "I don't trust the output I'm getting",
  [ProblemImpact.Cosmetic]: 'Cosmetic or confusing, but I can work around it',
};

export const PROBLEM_IMPACTS_WORST_FIRST: readonly ProblemImpact[] = [
  ProblemImpact.Blocked,
  ProblemImpact.RepeatedRework,
  ProblemImpact.SlowWorkaround,
  ProblemImpact.UntrustedOutput,
  ProblemImpact.Cosmetic,
];

/**
 * A "Report a Problem" submission from any Paragon client.
 *
 * Note that no identity fields are trusted from here: the server stamps name, email, and organization from the
 * caller's JWT claims. `reportedName` and `replyToEmail` are display/routing preferences only, used when someone is
 * on a shared login or wants a colleague copied. Neither is an address the server sends to: the acknowledgement
 * always goes to the account on the JWT, and support reads `replyToEmail` off the Slack message.
 */
export interface ProblemReport {
  /** What the customer was trying to accomplish. */
  readonly goal: string;
  /** What happened instead. */
  readonly whatHappened: string;
  readonly impact: ProblemImpact;

  /** Overrides the display name only if the customer edited it; the JWT identity is still recorded. */
  readonly reportedName: string | null;
  /** Where the customer wants support's reply to go, if not their account email. Shown to support, not mailed to. */
  readonly replyToEmail: string | null;

  /** The customer's location in the app — `window.location.href` as it stood when they opened the dialog. */
  readonly url: string;
  /** Human-readable version of what `url` points at, e.g. "Lot 208 Girder G3 · selected: Member". */
  readonly contextDescription: string | null;

  readonly clientName: string;
  readonly environment: string;
  readonly clientVersion: string | null;

  /** Something a human can read, e.g. "Chrome 150 on Windows". This is what appears in the Slack message. */
  readonly browserDescription: string;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  /** The customer's own clock, formatted in their locale and timezone — often the fastest way to find their session. */
  readonly localTimestamp: string;

  /** PNG data URL of the customer's viewport, or `null` if unavailable or opted out. */
  readonly screenshotPngDataUrl: string | null;
}
